// src/tools/sessions.ts
import fs from "node:fs/promises";
import path from "node:path";
import type { SessionState, ToolResult } from "../types.js";
import { err, okJson } from "../types.js";

/**
 * Read all session state files from `.skaile/sessions/` and return them
 * sorted newest-first by `updatedAt`.
 *
 * Silently skips missing directories and corrupt JSON files.  This function
 * is exported for internal use by `flows.ts` — prefer {@link sessionList} or
 * {@link sessionGet} for tool-level access.
 *
 * @param projectDir - Absolute path to the skaile project root.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function readAllSessions(projectDir: string): Promise<SessionState[]> {
  const dir = path.join(projectDir, ".skaile", "sessions");
  try {
    const files = await fs.readdir(dir);
    const sessions: SessionState[] = [];
    for (const file of files) {
      if (!file.endsWith(".json")) continue;
      try {
        const raw = await fs.readFile(path.join(dir, file), "utf-8");
        sessions.push(JSON.parse(raw) as SessionState);
      } catch {
        /* skip corrupt */
      }
    }
    return sessions.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  } catch {
    return [];
  }
}

/**
 * List all past agent run sessions for the workspace, sorted newest-first.
 *
 * Returns a JSON array of {@link SessionState} objects read from
 * `.skaile/sessions/*.json`.
 *
 * @param projectDir - Absolute path to the skaile project root.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function sessionList(projectDir: string): Promise<ToolResult> {
  try {
    const sessions = await readAllSessions(projectDir);
    return okJson(sessions);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Get full metadata for a specific agent run session.
 *
 * @param projectDir - Absolute path to the skaile project root.
 * @param runId      - Run ID as returned by {@link sessionList}.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function sessionGet(projectDir: string, runId: string): Promise<ToolResult> {
  try {
    const sessions = await readAllSessions(projectDir);
    const session = sessions.find((s) => s.runId === runId);
    if (!session) return err(`Session '${runId}' not found in ${projectDir}/.skaile/sessions/`);
    return okJson(session);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}
